import * as React from 'react';
import type { PluginExports } from 'graylog-web-plugin/plugin';

import {
  DATA_LAKE_INTERNAL_ENABLED,
  DATALAKE_ATTRIBUTE_NAME,
  DATALAKE_ATTRIBUTES,
  DATA_LAKE_PREVIEW_NAV_ITEM,
  DATA_LAKE_EXTERNAL_SEARCH_ENABLED,
  DATA_LAKE_PREVIEW_ENABLED,
  DATA_LAKE_ENABLED,
} from 'data-lake/Constants';
import DataLakesPage from 'data-lake/DataLakesPage';
import DataLakeBackendPage from 'data-lake/data-lake-backend-config/DataLakeBackendPage';
import { isPermitted } from 'util/PermissionsMixin';
import StreamDataLake from 'data-lake/stream/StreamDataLake';
import DataLakeJobs from 'data-lake/components/DataLakeJobs';
import dataLakeCustomColumnRenderer from 'data-lake/DataLakeColumnRenderer';
import DataLakeConfigPage from 'data-lake/DataLakeConfigPage';
import StreamIndexSetDataLakeWarning from 'data-lake/stream/StreamIndexSetDataLakeWarning';
import DataLakeStreamDeleteWarning from 'data-lake/stream/DataLakeStreamDeleteWarning';
import { fetchStreamDataLake } from 'data-lake/hooks/useStreamDataLake';
import { fetchStreamDataLakeConfig } from 'data-lake/hooks/useStreamDataLakeConfig';
import StreamIlluminateProcessingSection from 'data-lake/stream/StreamIlluminateProcessingSection';
import validLicensePresent from 'license/ValidLicensePresent';
import DataLakeQueryValidation from 'data-lake/DataLakeQueryValidation';
import DataLakeJournal from 'data-lake/components/DataLakeJournal';
import DataLakeStatus from 'data-lake/components/DataLakeStatus';
import DataLakePreviewPage from 'data-lake/preview/DataLakePreviewPage';
import App from 'routing/App';
import DataLakeRoutes from 'data-lake/Routes';
import DestinationPermissionAlert from 'components/streams/StreamDetails/DestinationPermissionAlert';
import AppConfig from 'util/AppConfig';
import DataLakeConnectorsPage from 'data-lake/data-lake-connectors/DataLakeConnectorsPage';
import DataLakeRetrievalsPage from 'data-lake/data-lake-retrievals/DataLakeRetrievalsPage';

const ROUTES = [
  ...(DATA_LAKE_EXTERNAL_SEARCH_ENABLED
    ? [
        {
          path: DataLakeRoutes.unqualified.CONNECTORS,
          component: DataLakeConnectorsPage,
          permissions: ['data_lake:read', 'data_lake_backend_config:read'],
        },
        {
          path: DataLakeRoutes.unqualified.RETRIEVALS,
          component: DataLakeRetrievalsPage,
          permissions: 'data_lake:read',
        },
      ]
    : []),
  ...(DATA_LAKE_PREVIEW_ENABLED
    ? [
        {
          path: DataLakeRoutes.unqualified.PREVIEW,
          component: DataLakePreviewPage,
          parentComponent: App,
        },
      ]
    : []),
];
const PREM_ONLY_ROUTES = DATA_LAKE_INTERNAL_ENABLED
  ? [
      {
        path: DataLakeRoutes.unqualified.ARCHIVE.LIST,
        component: DataLakesPage,
        permissions: 'data_lake:read',
      },
      {
        path: DataLakeRoutes.unqualified.BACKEND,
        component: DataLakeBackendPage,
        permissions: 'data_lake_backend_config:read',
      },
      {
        path: DataLakeRoutes.unqualified.CONFIGURATION,
        component: DataLakeConfigPage,
        permissions: 'data_lake_backend_config:read',
      },
    ]
  : [];

const routes = AppConfig.isCloud() ? ROUTES : [...ROUTES, ...PREM_ONLY_ROUTES];

const getStreamDataLakeTableElements = () => ({
  attributeName: DATALAKE_ATTRIBUTE_NAME,
  attributes: DATALAKE_ATTRIBUTES,
  columnRenderer: dataLakeCustomColumnRenderer,
});

const dataLakeExport: PluginExports = {
  'views.elements.validationErrorExplanation': [
    (props) => (validLicensePresent() ? <DataLakeQueryValidation {...props} /> : null),
  ],
  routes: routes,
  dataLake: DATA_LAKE_INTERNAL_ENABLED
    ? [
        {
          DataLakeJournal: DataLakeJournal,
          DataLakeStatus: DataLakeStatus,
          StreamDataLake: (props) =>
            isPermitted(props.permissions, 'data_lake:read') ? (
              <StreamDataLake />
            ) : (
              <DestinationPermissionAlert sectionName="Data Lake" />
            ),
          DataLakeJobs: (props) =>
            isPermitted(props.permissions, 'data_lake:read') ? <DataLakeJobs {...props} /> : null,
          StreamIndexSetDataLakeWarning: StreamIndexSetDataLakeWarning,
          fetchStreamDataLake: fetchStreamDataLake,
          fetchStreamDataLakeStatus: fetchStreamDataLakeConfig,
          DataLakeStreamDeleteWarning: DataLakeStreamDeleteWarning,
          getStreamDataLakeTableElements: getStreamDataLakeTableElements,
          StreamIlluminateProcessingSection,
        },
      ]
    : [],
  navigation: DATA_LAKE_ENABLED ? [DATA_LAKE_PREVIEW_NAV_ITEM] : [],
};

export default dataLakeExport;
